/**
 * The entry points that change one mark: where it sits, where it points, how
 * its line is drawn, and the conventions the whole set is drawn under.
 *
 * Apart from the rest because these are the ones a drag produces, many per
 * gesture, while the others are one deliberate act each. Every import here is
 * type-only, so nothing is loaded back from the module that calls it.
 */

import type { PointModel } from "@patent-studio/api-types";
import type {
  ConnectorDefaults,
  ConnectorShape,
  Definitions,
  LineKind,
  NumberingConvention,
  NumeralStyle,
} from "@/lib/element-registry";
import type { DefinitionChange } from "./use-figure-definitions";

export function markEntryPoints(
  issue: (change: DefinitionChange) => void,
  showing: () => Definitions,
) {
  return {
    removePin: (fileId: string, pin: { id: string }) =>
      issue({ kind: "remove-pin", fileId, id: pin.id }),

    // The six below all name one mark by its id and state what it becomes, and the rules
    // behind them hand the set back untouched when the mark they name has moved on. So a
    // change queued behind another that took its line away, or its curve, or the mark
    // itself, costs a write and changes nothing rather than building a set the store
    // refuses. Only the restore below reads the view, and only for the conventions in
    // force, which is a set-level answer no drag can carry.
    movePin: (fileId: string, drag: { id: string; pin: PointModel }) =>
      issue({ kind: "move-pin", fileId, ...drag }),

    aimTip: (fileId: string, drag: { id: string; tip: PointModel }) =>
      issue({ kind: "aim-tip", fileId, ...drag }),

    bend: (
      fileId: string,
      drag: { id: string; index: number; control: PointModel },
    ) => issue({ kind: "bend", fileId, ...drag }),

    setConnectorShape: (
      fileId: string,
      choice: {
        id: string;
        shape: ConnectorShape;
        anchors: ConnectorDefaults["anchors"];
      },
    ) =>
      issue({
        kind: "connector-shape",
        fileId,
        ...choice,
        defaults: showing().settings.connector_defaults,
      }),

    setArrowhead: (fileId: string, choice: { id: string; on: boolean }) =>
      issue({ kind: "connector-arrowhead", fileId, ...choice }),

    setConnectorPresence: (
      fileId: string,
      choice: { id: string; present: boolean },
    ) =>
      issue({
        kind: "connector-presence",
        fileId,
        ...choice,
        defaults: showing().settings.connector_defaults,
      }),

    /**
     * One press of the line control, said in the two rules the store keeps.
     *
     * A line is either there or not, and a line that is there has a shape, so asking for a
     * curve on a mark with no line is asking for both. The two writes drain separately and
     * either can fail alone with no rollback of the other, so the one that gives the line
     * back already carries the line that was pressed: the shape change behind it then only
     * ever settles as nothing, and losing it leaves the mark as the attorney asked rather
     * than wearing a bend they never chose.
     */
    setLineKind: (fileId: string, choice: { id: string; kind: LineKind }) => {
      const defaults = showing().settings.connector_defaults;
      if (choice.kind === "none") {
        issue({
          kind: "connector-presence",
          fileId,
          id: choice.id,
          present: false,
          defaults,
        });
        return;
      }
      const shape = choice.kind === "straight" ? "straight" : "curved";
      const anchors = choice.kind === "s-curve" ? 2 : 1;
      issue({
        kind: "connector-presence",
        fileId,
        id: choice.id,
        present: true,
        defaults: { ...defaults, shape, anchors },
      });
      issue({
        kind: "connector-shape",
        fileId,
        id: choice.id,
        shape,
        anchors,
        defaults,
      });
    },
    ...settingsEntryPoints(issue),
  };
}

/** The entry points that change a convention the whole set is drawn under. */
export function settingsEntryPoints(issue: (change: DefinitionChange) => void) {
  return {
    setConnectorDefaults: (defaults: ConnectorDefaults) =>
      issue({ kind: "connector-defaults", defaults }),

    setNumeralStyle: (style: NumeralStyle) =>
      issue({ kind: "numeral-style", style }),

    setNumberingConvention: (numbering_convention: NumberingConvention) =>
      issue({ kind: "numbering-convention", numbering_convention }),

    setMarkNumeralStyle: (fileId: string, id: string, style: NumeralStyle) =>
      issue({ kind: "mark-numeral-style", fileId, id, style }),
  };
}
